/**
 * i18n React 整合 — `<I18nProvider>` + `useI18n()`。
 *
 * locale 變更會觸發 re-render（context value 換新），全頁 `t()` 同步切換。
 * locale 同步寫 localStorage（`storeLocale`），下次開頁延續，且 `api-client`
 * 之 `Accept-Language` 透過 `getStoredLocale()` 取到同一值。
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { getStoredLocale, storeLocale, translate, type Locale, type TranslationKey } from './i18n';

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** 取 UI 字串；params 供 `{name}` 插值。 */
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(() => getStoredLocale());

  const setLocale = useCallback((next: Locale): void => {
    setLocaleState(next);
    storeLocale(next);
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t: (key, params) => translate(locale, key, params),
    }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (ctx === null) {
    throw new Error('useI18n 必須在 <I18nProvider> 之內使用');
  }
  return ctx;
}
